import { createAsyncThunk } from '@reduxjs/toolkit';

import { IInvoice, TInvoiceStatus } from '.';
import {
  AdminApiPaths,
  APIDataProvider,
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api';
import { TFlexiblePlanExpiredInTypes } from '../FlexiblePlansSlice';

export type TAdminCreateStripeInvoice = {
  userId: string;
  currency: string;
  isCreditChecked: boolean;
  isCourseCreditChecked: boolean;
  creditQuantity?: number;
  creditRate?: number;
  creditDescription?: string;
  isCustomChecked: boolean;
  expiryDate: string;
  customAmounts?: Array<{
    description: string;
    quantity: number;
    rate: number;
  }>;
  expiredIn?: {
    count: number;
    type: TFlexiblePlanExpiredInTypes;
  };
  courseId?: string;
  courseCreditQuantity?: number;
};

export type TAdminGetInvoices = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  userId?: string;
  status?: TInvoiceStatus;
  invoiceNumber?: string;
};

export type TAdminUpdateInvoice = {
  id: string;
  status?: IInvoice['status'];
};

export type TAdminSendInvoiceMail = {
  id: string;
};

export type TAdminInvoiceCreateAdminNote = {
  id: string;
  description: string;
};

export type TAdminInvoiceDeleteAdminNote = {
  id: string;
  noteId: string;
};

const apiDataProvider = new APIDataProvider();

const adminGetInvoicesModule = async ({
  limit,
  offset,
  sortKey,
  sortValue,
  userId,
  status,
  invoiceNumber,
}: TAdminGetInvoices) => {
  let path = `${AdminApiPaths.invoices}?limit=${limit}&offset=${offset}`;

  if (sortKey) {
    path += `&sortKey=${sortKey}`;
  }

  if (sortKey) {
    path += `&sortValue=${sortValue}`;
  }

  if (userId) {
    path += `&userId=${userId}`;
  }

  if (status) {
    path += `&status=${status}`;
  }

  if (invoiceNumber) {
    path += `&invoiceNumber=${invoiceNumber}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<IInvoice>>
  >(path);

  return response.data;
};

export const adminCreateStripeInvoice = createAsyncThunk(
  '/admin/invoices/stripe/create',
  async (data: TAdminCreateStripeInvoice) => {
    const response = await apiDataProvider.post<TApiResponse<IInvoice>>(
      `${AdminApiPaths.invoices}/stripe`,
      data
    );

    return response.data;
  }
);

export const adminGetInvoices = createAsyncThunk(
  '/admin/invoices/list',
  adminGetInvoicesModule
);

export const adminUpdateInvoice = createAsyncThunk(
  '/admin/invoices/updaetOne',
  async (data: TAdminUpdateInvoice) => {
    const response = await apiDataProvider.put<TApiResponse<IInvoice>>(
      `${AdminApiPaths.invoices}/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminSendInvoiceMail = createAsyncThunk(
  '/admin/invoices/mail/send',
  async (data: TAdminSendInvoiceMail) => {
    const response = await apiDataProvider.post<
      TApiResponse<{ status: 'success' }>
    >(`${AdminApiPaths.invoices}/send-mail/${data.id}`, data);

    return response.data;
  }
);

export const adminCreateInvoiceAdminNote = createAsyncThunk(
  '/admin/invoices/admin-notes/create',
  async (data: TAdminInvoiceCreateAdminNote) => {
    const response = await apiDataProvider.post<TApiResponse<IInvoice>>(
      `${AdminApiPaths.invoices}/admin-notes/${data.id}`,
      data
    );

    return response.data;
  }
);

export const adminDeleteInvoiceAdminNote = createAsyncThunk(
  '/admin/invoices/admin-notes/delete',
  async ({ id, noteId }: TAdminInvoiceDeleteAdminNote) => {
    const response = await apiDataProvider.delete<TApiResponse<IInvoice>>(
      `${AdminApiPaths.invoices}/admin-notes/${id}?id=${id}&noteId=${noteId}`
    );

    return response.data;
  }
);
